import type { SharedResultLocale } from "./shared-result-contract.ts";

const en = {
  sharedTitle: "Shared backtest evidence",
  sharedDescription: "A read-only snapshot of historical backtest evidence from Backtest Trading.",
  readOnly: "View only",
  summaryOnly: "Summary only — strategy rules are not included",
  reproducible: "Reusable strategy — review and rerun with your own assumptions",
  duplicateStrategy: "Duplicate strategy",
  duplicateHint: "This copies the published rules into a new editable simulation. It never places a trade.",
  duplicateFailed: "The strategy could not be prepared. Try again later.",
  reportResult: "Report result",
  reportReason: "Reason",
  reportReasonMisleading: "Misleading performance",
  reportReasonSpam: "Spam",
  reportReasonUnsafe: "Unsafe content",
  reportReasonOther: "Other",
  reportDetails: "Optional details",
  reportSubmit: "Send report",
  reportReceived: "Report received. Thank you for helping keep shared research useful.",
  reportDetailsNotRetained: "Report accepted, but optional details were not retained. Contact safety support if those details are important.",
  reportFailed: "The report could not be sent. Check your connection and try again.",
  blockResult: "Block this result",
  blockedTitle: "This shared result is blocked on this device",
  blockedBody: "You can leave this page or run a new independent backtest.",
  moderationContact: "Safety contact",
  cancel: "Cancel",
  linkAccessNote: "Anyone with this unlisted link can view the summary until it expires or the owner revokes it.",
  openInApp: "Open in Backtest Trading",
  openHint: "If this page stays in Safari, open the original link from Messages or Mail, or get the app from the App Store.",
  viewOnAppStore: "View on the App Store",
  loadingTitle: "Loading shared evidence",
  loadingBody: "Checking this unlisted link and preparing its read-only report…",
  notFoundTitle: "This shared result is unavailable",
  notFoundBody: "The link may be incorrect, unavailable, or revoked by its owner.",
  expiredTitle: "This shared result has expired",
  expiredBody: "Its evidence snapshot is no longer available. Ask the owner to create a new link.",
  unavailableTitle: "Shared results are temporarily unavailable",
  unavailableBody: "The report service could not respond. Your link has not been changed.",
  retry: "Try again",
  createdAt: "Snapshot created",
  expiresAt: "Available until",
  testedPeriod: "Tested period",
  returnMetric: "Return",
  maxDrawdownMetric: "Max drawdown",
  tradesMetric: "Trades",
  riskNote: "Historical simulation is descriptive evidence, not a forecast or investment advice.",
  equityCurve: "Normalized equity curve",
  equityDescription: "Percentage return over the tested period; no capital amounts or trade timestamps are shared.",
  methodologyBody: "Review how Backtest calculates results and applies execution assumptions.",
  homepage: "Backtest homepage",
  newBacktest: "Run a new backtest",
} as const;

export type SharedResultTextKey = keyof typeof en;
type LocalizedStrings = Partial<Record<SharedResultTextKey, string>>;

const translations: Record<Exclude<SharedResultLocale, "en">, LocalizedStrings> = {
  vi: {
    sharedTitle: "Bằng chứng backtest được chia sẻ",
    sharedDescription: "Ảnh chụp chỉ đọc của bằng chứng backtest lịch sử từ Backtest Trading.",
    readOnly: "Chỉ xem",
    summaryOnly: "Chỉ có tóm tắt — không bao gồm quy tắc chiến lược",
    reproducible: "Chiến lược có thể dùng lại — hãy kiểm tra và chạy lại với giả định của bạn",
    duplicateStrategy: "Nhân bản chiến lược",
    duplicateHint: "Thao tác này sao chép bộ quy tắc đã công bố vào mô phỏng mới có thể chỉnh sửa. Ứng dụng không đặt lệnh giao dịch.",
    duplicateFailed: "Không thể chuẩn bị chiến lược. Vui lòng thử lại sau.",
    reportResult: "Báo cáo kết quả",
    reportReason: "Lý do",
    reportDetails: "Chi tiết không bắt buộc",
    reportSubmit: "Gửi báo cáo",
    reportReceived: "Đã nhận báo cáo. Cảm ơn bạn đã giúp nội dung nghiên cứu hữu ích hơn.",
    blockResult: "Chặn kết quả này",
    blockedTitle: "Kết quả chia sẻ này đã bị chặn trên thiết bị",
    blockedBody: "Bạn có thể rời trang hoặc chạy một backtest độc lập mới.",
    moderationContact: "Liên hệ an toàn",
    cancel: "Hủy",
    linkAccessNote: "Bất kỳ ai có liên kết không công khai này đều có thể xem phần tóm tắt cho đến khi link hết hạn hoặc bị chủ sở hữu thu hồi.",
    openInApp: "Mở trong Backtest Trading",
    openHint: "Nếu trang vẫn ở Safari, hãy mở liên kết gốc từ Tin nhắn hoặc Mail, hoặc tải app trên App Store.",
    viewOnAppStore: "Xem trên App Store",
    loadingTitle: "Đang tải bằng chứng được chia sẻ",
    loadingBody: "Đang kiểm tra liên kết không công khai và chuẩn bị báo cáo chỉ đọc…",
    notFoundTitle: "Kết quả được chia sẻ không khả dụng",
    notFoundBody: "Liên kết có thể không đúng, không còn khả dụng hoặc đã bị chủ sở hữu thu hồi.",
    expiredTitle: "Kết quả được chia sẻ đã hết hạn",
    expiredBody: "Ảnh chụp bằng chứng không còn khả dụng. Hãy nhờ chủ sở hữu tạo liên kết mới.",
    unavailableTitle: "Kết quả chia sẻ đang tạm gián đoạn",
    unavailableBody: "Dịch vụ báo cáo chưa thể phản hồi. Liên kết của bạn không bị thay đổi.",
    retry: "Thử lại",
    createdAt: "Tạo ảnh chụp lúc",
    expiresAt: "Khả dụng đến",
    testedPeriod: "Giai đoạn kiểm thử",
    returnMetric: "Tỷ suất",
    maxDrawdownMetric: "Sụt giảm tối đa",
    tradesMetric: "Lệnh",
    riskNote: "Mô phỏng lịch sử chỉ là bằng chứng mô tả, không dự báo kết quả và không phải lời khuyên đầu tư.",
    equityCurve: "Đường vốn chuẩn hóa",
    equityDescription: "Lợi nhuận phần trăm trong giai đoạn kiểm thử; không chia sẻ số vốn hay thời điểm giao dịch.",
    methodologyBody: "Xem cách Backtest tính kết quả và áp dụng các giả định khớp lệnh.",
    homepage: "Trang chủ Backtest",
    newBacktest: "Chạy backtest mới",
  },
  hi: {
    sharedTitle: "साझा बैकटेस्ट प्रमाण",
    sharedDescription: "Backtest Trading से ऐतिहासिक बैकटेस्ट प्रमाण का केवल-पढ़ने योग्य स्नैपशॉट।",
    readOnly: "केवल देखें",
    summaryOnly: "केवल सारांश — रणनीति के नियम शामिल नहीं हैं",
    linkAccessNote: "इस अनलिस्टेड लिंक वाला कोई भी व्यक्ति इसके समाप्त होने या मालिक द्वारा रद्द किए जाने तक सारांश देख सकता है।",
    openInApp: "Backtest Trading में खोलें",
    openHint: "यदि पेज Safari में ही रहे, तो मूल लिंक Messages या Mail से खोलें, या App Store से ऐप लें।",
    viewOnAppStore: "App Store पर देखें",
    loadingTitle: "साझा प्रमाण लोड हो रहा है",
    loadingBody: "अनलिस्टेड लिंक की जाँच करके केवल-पढ़ने योग्य रिपोर्ट तैयार की जा रही है…",
    notFoundTitle: "यह साझा परिणाम उपलब्ध नहीं है",
    notFoundBody: "लिंक गलत, अनुपलब्ध या मालिक द्वारा रद्द किया गया हो सकता है।",
    expiredTitle: "यह साझा परिणाम समाप्त हो गया है",
    expiredBody: "प्रमाण स्नैपशॉट अब उपलब्ध नहीं है। मालिक से नया लिंक बनाने को कहें।",
    unavailableTitle: "साझा परिणाम अस्थायी रूप से उपलब्ध नहीं हैं",
    unavailableBody: "रिपोर्ट सेवा उत्तर नहीं दे सकी। आपका लिंक बदला नहीं गया है।",
    retry: "फिर प्रयास करें",
    createdAt: "स्नैपशॉट बनाया गया",
    expiresAt: "यहाँ तक उपलब्ध",
    testedPeriod: "परीक्षण अवधि",
    returnMetric: "रिटर्न",
    maxDrawdownMetric: "अधिकतम ड्रॉडाउन",
    tradesMetric: "ट्रेड",
    riskNote: "ऐतिहासिक सिमुलेशन केवल वर्णनात्मक प्रमाण है; यह पूर्वानुमान या निवेश सलाह नहीं है।",
    equityCurve: "सामान्यीकृत इक्विटी कर्व",
    equityDescription: "परीक्षण अवधि का प्रतिशत रिटर्न; पूंजी राशि या ट्रेड समय साझा नहीं किए जाते।",
    methodologyBody: "देखें कि Backtest परिणाम और execution assumptions कैसे लागू करता है।",
    homepage: "Backtest होमपेज",
    newBacktest: "नया बैकटेस्ट चलाएँ",
  },
  ur: {
    sharedTitle: "شیئر کردہ بیک ٹیسٹ ثبوت",
    sharedDescription: "Backtest Trading سے تاریخی بیک ٹیسٹ ثبوت کا صرف دیکھنے کے لیے اسنیپ شاٹ۔",
    readOnly: "صرف دیکھیں",
    summaryOnly: "صرف خلاصہ — حکمتِ عملی کے قواعد شامل نہیں",
    linkAccessNote: "اس ان لسٹڈ لنک والا کوئی بھی شخص اس کے ختم ہونے یا مالک کے منسوخ کرنے تک خلاصہ دیکھ سکتا ہے۔",
    openInApp: "Backtest Trading میں کھولیں",
    openHint: "اگر صفحہ Safari میں رہے تو اصل لنک Messages یا Mail سے کھولیں، یا App Store سے ایپ حاصل کریں۔",
    viewOnAppStore: "App Store پر دیکھیں",
    loadingTitle: "شیئر کردہ ثبوت لوڈ ہو رہا ہے",
    loadingBody: "ان لسٹڈ لنک چیک کرکے صرف پڑھنے کی رپورٹ تیار کی جا رہی ہے…",
    notFoundTitle: "یہ شیئر کردہ نتیجہ دستیاب نہیں",
    notFoundBody: "لنک غلط، غیر دستیاب، یا مالک کی جانب سے منسوخ کیا گیا ہو سکتا ہے۔",
    expiredTitle: "یہ شیئر کردہ نتیجہ ختم ہو چکا ہے",
    expiredBody: "ثبوت کا اسنیپ شاٹ اب دستیاب نہیں۔ مالک سے نیا لنک بنانے کو کہیں۔",
    unavailableTitle: "شیئر کردہ نتائج عارضی طور پر دستیاب نہیں",
    unavailableBody: "رپورٹ سروس جواب نہیں دے سکی۔ آپ کا لنک تبدیل نہیں ہوا۔",
    retry: "دوبارہ کوشش کریں",
    createdAt: "اسنیپ شاٹ بنایا گیا",
    expiresAt: "یہاں تک دستیاب",
    testedPeriod: "آزمائشی مدت",
    returnMetric: "واپسی",
    maxDrawdownMetric: "زیادہ سے زیادہ ڈرا ڈاؤن",
    tradesMetric: "ٹریڈز",
    riskNote: "تاریخی simulation صرف وضاحتی ثبوت ہے، پیش گوئی یا سرمایہ کاری کا مشورہ نہیں۔",
    equityCurve: "نارملائزڈ ایکویٹی کرو",
    equityDescription: "آزمائشی مدت کی فیصد واپسی؛ سرمایہ یا ٹریڈ کے اوقات شیئر نہیں ہوتے۔",
    methodologyBody: "دیکھیں کہ Backtest نتائج اور execution assumptions کیسے لاگو کرتا ہے۔",
    homepage: "Backtest ہوم پیج",
    newBacktest: "نیا بیک ٹیسٹ چلائیں",
  },
  th: {
    sharedTitle: "หลักฐาน Backtest ที่แชร์",
    sharedDescription: "ภาพสรุปแบบอ่านอย่างเดียวของหลักฐาน Backtest ย้อนหลังจาก Backtest Trading",
    readOnly: "ดูอย่างเดียว",
    summaryOnly: "สรุปเท่านั้น — ไม่รวมกฎของกลยุทธ์",
    linkAccessNote: "ทุกคนที่มีลิงก์แบบไม่แสดงรายการนี้สามารถดูสรุปได้จนกว่าลิงก์จะหมดอายุหรือเจ้าของเพิกถอน",
    openInApp: "เปิดใน Backtest Trading",
    openHint: "หากหน้ายังคงอยู่ใน Safari ให้เปิดลิงก์ต้นฉบับจาก Messages หรือ Mail หรือดาวน์โหลดแอปจาก App Store",
    viewOnAppStore: "ดูบน App Store",
    loadingTitle: "กำลังโหลดหลักฐานที่แชร์",
    loadingBody: "กำลังตรวจสอบลิงก์แบบไม่แสดงรายการและเตรียมรายงานแบบอ่านอย่างเดียว…",
    notFoundTitle: "ผลลัพธ์ที่แชร์นี้ไม่พร้อมใช้งาน",
    notFoundBody: "ลิงก์อาจไม่ถูกต้อง ไม่พร้อมใช้งาน หรือถูกเจ้าของเพิกถอนแล้ว",
    expiredTitle: "ผลลัพธ์ที่แชร์นี้หมดอายุแล้ว",
    expiredBody: "ภาพหลักฐานไม่พร้อมใช้งานแล้ว โปรดขอให้เจ้าของสร้างลิงก์ใหม่",
    unavailableTitle: "ผลลัพธ์ที่แชร์ไม่พร้อมใช้งานชั่วคราว",
    unavailableBody: "บริการรายงานไม่ตอบสนอง ลิงก์ของคุณไม่ได้ถูกเปลี่ยนแปลง",
    retry: "ลองอีกครั้ง",
    createdAt: "สร้างภาพเมื่อ",
    expiresAt: "ใช้ได้ถึง",
    testedPeriod: "ช่วงที่ทดสอบ",
    returnMetric: "ผลตอบแทน",
    maxDrawdownMetric: "การขาดทุนสูงสุด",
    tradesMetric: "คำสั่ง",
    riskNote: "การจำลองจากข้อมูลย้อนหลังเป็นเพียงหลักฐานเชิงพรรณนา ไม่ใช่การคาดการณ์หรือคำแนะนำการลงทุน",
    equityCurve: "เส้น Equity ที่ปรับฐานแล้ว",
    equityDescription: "ผลตอบแทนเป็นเปอร์เซ็นต์ตลอดช่วงทดสอบ โดยไม่แชร์จำนวนเงินทุนหรือเวลาเทรด",
    methodologyBody: "ดูวิธีที่ Backtest คำนวณผลลัพธ์และใช้สมมติฐานการส่งคำสั่ง",
    homepage: "หน้าแรก Backtest",
    newBacktest: "รัน Backtest ใหม่",
  },
  bn: {
    sharedTitle: "শেয়ার করা ব্যাকটেস্ট প্রমাণ",
    sharedDescription: "Backtest Trading-এর ঐতিহাসিক ব্যাকটেস্ট প্রমাণের শুধু-পঠনযোগ্য স্ন্যাপশট।",
    readOnly: "শুধু দেখুন",
    summaryOnly: "শুধু সারাংশ — কৌশলের নিয়ম অন্তর্ভুক্ত নয়",
    linkAccessNote: "এই আনলিস্টেড লিংক যার কাছে আছে, মেয়াদ শেষ বা মালিক প্রত্যাহার না করা পর্যন্ত তিনি সারাংশ দেখতে পারবেন।",
    openInApp: "Backtest Trading-এ খুলুন",
    openHint: "পৃষ্ঠা Safari-তেই থাকলে Messages বা Mail থেকে মূল লিংক খুলুন, অথবা App Store থেকে অ্যাপ নিন।",
    viewOnAppStore: "App Store-এ দেখুন",
    loadingTitle: "শেয়ার করা প্রমাণ লোড হচ্ছে",
    loadingBody: "আনলিস্টেড লিংক যাচাই করে শুধু-পঠনযোগ্য রিপোর্ট প্রস্তুত করা হচ্ছে…",
    notFoundTitle: "এই শেয়ার করা ফল পাওয়া যাচ্ছে না",
    notFoundBody: "লিংকটি ভুল, অনুপলভ্য বা মালিক প্রত্যাহার করে থাকতে পারেন।",
    expiredTitle: "এই শেয়ার করা ফলের মেয়াদ শেষ",
    expiredBody: "প্রমাণের স্ন্যাপশট আর পাওয়া যাচ্ছে না। মালিককে নতুন লিংক তৈরি করতে বলুন।",
    unavailableTitle: "শেয়ার করা ফল সাময়িকভাবে পাওয়া যাচ্ছে না",
    unavailableBody: "রিপোর্ট সেবা সাড়া দেয়নি। আপনার লিংক পরিবর্তিত হয়নি।",
    retry: "আবার চেষ্টা করুন",
    createdAt: "স্ন্যাপশট তৈরি",
    expiresAt: "যতদিন পাওয়া যাবে",
    testedPeriod: "পরীক্ষার সময়কাল",
    returnMetric: "রিটার্ন",
    maxDrawdownMetric: "সর্বোচ্চ ড্রডাউন",
    tradesMetric: "ট্রেড",
    riskNote: "ঐতিহাসিক সিমুলেশন শুধু বর্ণনামূলক প্রমাণ; এটি পূর্বাভাস বা বিনিয়োগ পরামর্শ নয়।",
    equityCurve: "স্বাভাবিকীকৃত ইকুইটি কার্ভ",
    equityDescription: "পরীক্ষার সময়ের শতাংশ রিটার্ন; মূলধনের পরিমাণ বা ট্রেডের সময় শেয়ার হয় না।",
    methodologyBody: "Backtest কীভাবে ফল গণনা ও execution assumptions প্রয়োগ করে তা দেখুন।",
    homepage: "Backtest হোমপেজ",
    newBacktest: "নতুন ব্যাকটেস্ট চালান",
  },
  id: {
    sharedTitle: "Bukti backtest yang dibagikan",
    sharedDescription: "Snapshot hanya-baca dari bukti backtest historis di Backtest Trading.",
    readOnly: "Hanya lihat",
    summaryOnly: "Hanya ringkasan — aturan strategi tidak disertakan",
    linkAccessNote: "Siapa pun yang memiliki tautan tak terdaftar ini dapat melihat ringkasan hingga kedaluwarsa atau dicabut pemiliknya.",
    openInApp: "Buka di Backtest Trading",
    openHint: "Jika halaman tetap di Safari, buka tautan asli dari Messages atau Mail, atau unduh aplikasi di App Store.",
    viewOnAppStore: "Lihat di App Store",
    loadingTitle: "Memuat bukti yang dibagikan",
    loadingBody: "Memeriksa tautan tak terdaftar dan menyiapkan laporan hanya-baca…",
    notFoundTitle: "Hasil yang dibagikan tidak tersedia",
    notFoundBody: "Tautan mungkin salah, tidak tersedia, atau telah dicabut oleh pemiliknya.",
    expiredTitle: "Hasil yang dibagikan telah kedaluwarsa",
    expiredBody: "Snapshot bukti tidak lagi tersedia. Minta pemilik membuat tautan baru.",
    unavailableTitle: "Hasil bersama untuk sementara tidak tersedia",
    unavailableBody: "Layanan laporan tidak merespons. Tautan Anda tidak berubah.",
    retry: "Coba lagi",
    createdAt: "Snapshot dibuat",
    expiresAt: "Tersedia hingga",
    testedPeriod: "Periode pengujian",
    returnMetric: "Imbal hasil",
    maxDrawdownMetric: "Drawdown maksimum",
    tradesMetric: "Trade",
    riskNote: "Simulasi historis hanyalah bukti deskriptif, bukan prakiraan atau saran investasi.",
    equityCurve: "Kurva ekuitas ternormalisasi",
    equityDescription: "Persentase return selama periode uji; nilai modal dan waktu trade tidak dibagikan.",
    methodologyBody: "Pelajari cara Backtest menghitung hasil dan menerapkan asumsi eksekusi.",
    homepage: "Beranda Backtest",
    newBacktest: "Jalankan backtest baru",
  },
  fil: {
    sharedTitle: "Ibinahaging backtest evidence",
    sharedDescription: "Read-only snapshot ng historical backtest evidence mula sa Backtest Trading.",
    readOnly: "View only",
    summaryOnly: "Summary lamang — hindi kasama ang strategy rules",
    linkAccessNote: "Makikita ng sinumang may unlisted link na ito ang summary hanggang mag-expire o bawiin ito ng owner.",
    openInApp: "Buksan sa Backtest Trading",
    openHint: "Kung manatili sa Safari ang page, buksan ang original link mula sa Messages o Mail, o kunin ang app sa App Store.",
    viewOnAppStore: "Tingnan sa App Store",
    loadingTitle: "Nilo-load ang shared evidence",
    loadingBody: "Sinusuri ang unlisted link at inihahanda ang read-only report…",
    notFoundTitle: "Hindi available ang shared result na ito",
    notFoundBody: "Maaaring mali, unavailable, o binawi na ng owner ang link.",
    expiredTitle: "Nag-expire na ang shared result",
    expiredBody: "Hindi na available ang evidence snapshot. Hilingin sa owner na gumawa ng bagong link.",
    unavailableTitle: "Pansamantalang hindi available ang shared results",
    unavailableBody: "Hindi sumagot ang report service. Hindi nabago ang iyong link.",
    retry: "Subukan muli",
    createdAt: "Ginawa ang snapshot",
    expiresAt: "Available hanggang",
    testedPeriod: "Tested period",
    returnMetric: "Kita",
    maxDrawdownMetric: "Pinakamalaking drawdown",
    tradesMetric: "Mga trade",
    riskNote: "Ang historical simulation ay deskriptibong ebidensya lamang, hindi hula o payo sa pamumuhunan.",
    equityCurve: "Normalized equity curve",
    equityDescription: "Percentage return sa tested period; walang capital amounts o trade timestamps na ibinabahagi.",
    methodologyBody: "Tingnan kung paano kinakalkula ng Backtest ang results at execution assumptions.",
    homepage: "Backtest homepage",
    newBacktest: "Magpatakbo ng bagong backtest",
  },
  "es-419": {
    sharedTitle: "Evidencia de backtest compartida",
    sharedDescription: "Instantánea de solo lectura de evidencia histórica de Backtest Trading.",
    readOnly: "Solo lectura",
    summaryOnly: "Solo resumen — no incluye las reglas de la estrategia",
    linkAccessNote: "Cualquier persona con este enlace no listado puede ver el resumen hasta que venza o el propietario lo revoque.",
    openInApp: "Abrir en Backtest Trading",
    openHint: "Si la página permanece en Safari, abre el enlace original desde Mensajes o Mail, o descarga la app en App Store.",
    viewOnAppStore: "Ver en App Store",
    loadingTitle: "Cargando evidencia compartida",
    loadingBody: "Comprobando el enlace no listado y preparando el reporte de solo lectura…",
    notFoundTitle: "Este resultado compartido no está disponible",
    notFoundBody: "El enlace puede ser incorrecto, no estar disponible o haber sido revocado por su propietario.",
    expiredTitle: "Este resultado compartido venció",
    expiredBody: "La instantánea ya no está disponible. Pide al propietario que cree un enlace nuevo.",
    unavailableTitle: "Los resultados compartidos no están disponibles temporalmente",
    unavailableBody: "El servicio de reportes no respondió. Tu enlace no ha cambiado.",
    retry: "Intentar de nuevo",
    createdAt: "Instantánea creada",
    expiresAt: "Disponible hasta",
    testedPeriod: "Periodo probado",
    returnMetric: "Retorno",
    maxDrawdownMetric: "Caída máxima",
    tradesMetric: "Operaciones",
    riskNote: "La simulación histórica es evidencia descriptiva, no un pronóstico ni asesoría de inversión.",
    equityCurve: "Curva de capital normalizada",
    equityDescription: "Retorno porcentual durante el periodo; no se comparten montos de capital ni horas de operaciones.",
    methodologyBody: "Consulta cómo Backtest calcula resultados y aplica supuestos de ejecución.",
    homepage: "Inicio de Backtest",
    newBacktest: "Ejecutar otro backtest",
  },
  "pt-BR": {
    sharedTitle: "Evidência de backtest compartilhada",
    sharedDescription: "Snapshot somente leitura de evidência histórica do Backtest Trading.",
    readOnly: "Somente leitura",
    summaryOnly: "Apenas resumo — as regras da estratégia não estão incluídas",
    linkAccessNote: "Qualquer pessoa com este link não listado pode ver o resumo até ele expirar ou ser revogado pelo proprietário.",
    openInApp: "Abrir no Backtest Trading",
    openHint: "Se a página continuar no Safari, abra o link original pelo Mensagens ou Mail, ou baixe o app na App Store.",
    viewOnAppStore: "Ver na App Store",
    loadingTitle: "Carregando evidência compartilhada",
    loadingBody: "Verificando o link não listado e preparando o relatório somente leitura…",
    notFoundTitle: "Este resultado compartilhado não está disponível",
    notFoundBody: "O link pode estar incorreto, indisponível ou ter sido revogado pelo proprietário.",
    expiredTitle: "Este resultado compartilhado expirou",
    expiredBody: "O snapshot não está mais disponível. Peça ao proprietário para criar um novo link.",
    unavailableTitle: "Resultados compartilhados estão temporariamente indisponíveis",
    unavailableBody: "O serviço de relatório não respondeu. Seu link não foi alterado.",
    retry: "Tentar novamente",
    createdAt: "Snapshot criado",
    expiresAt: "Disponível até",
    testedPeriod: "Período testado",
    returnMetric: "Retorno",
    maxDrawdownMetric: "Queda máxima",
    tradesMetric: "Operações",
    riskNote: "A simulação histórica é uma evidência descritiva, não uma previsão nem recomendação de investimento.",
    equityCurve: "Curva de patrimônio normalizada",
    equityDescription: "Retorno percentual no período; valores de capital e horários das operações não são compartilhados.",
    methodologyBody: "Veja como o Backtest calcula resultados e aplica premissas de execução.",
    homepage: "Início do Backtest",
    newBacktest: "Rodar novo backtest",
  },
  ja: {
    sharedTitle: "共有されたバックテスト証拠",
    sharedDescription: "Backtest Tradingによる過去のバックテスト証拠の読み取り専用スナップショットです。",
    readOnly: "閲覧のみ",
    summaryOnly: "概要のみ — 戦略ルールは含まれません",
    linkAccessNote: "この限定公開リンクを知っている人は、期限切れまたは所有者が取り消すまで概要を閲覧できます。",
    openInApp: "Backtest Tradingで開く",
    openHint: "Safariのままの場合は、メッセージまたはメールから元のリンクを開くか、App Storeからアプリを入手してください。",
    viewOnAppStore: "App Storeで見る",
    loadingTitle: "共有証拠を読み込み中",
    loadingBody: "限定公開リンクを確認し、読み取り専用レポートを準備しています…",
    notFoundTitle: "この共有結果は利用できません",
    notFoundBody: "リンクが間違っている、利用できない、または所有者により取り消された可能性があります。",
    expiredTitle: "この共有結果は期限切れです",
    expiredBody: "証拠スナップショットは利用できません。所有者に新しいリンクを依頼してください。",
    unavailableTitle: "共有結果は一時的に利用できません",
    unavailableBody: "レポートサービスが応答しませんでした。リンクは変更されていません。",
    retry: "再試行",
    createdAt: "スナップショット作成日時",
    expiresAt: "利用期限",
    testedPeriod: "テスト期間",
    returnMetric: "リターン",
    maxDrawdownMetric: "最大ドローダウン",
    tradesMetric: "取引数",
    riskNote: "過去データのシミュレーションは説明的な証拠にすぎず、予測や投資助言ではありません。",
    equityCurve: "正規化エクイティカーブ",
    equityDescription: "テスト期間の収益率です。資金額や取引時刻は共有されません。",
    methodologyBody: "Backtestの結果計算と約定前提を確認します。",
    homepage: "Backtestホーム",
    newBacktest: "新しいバックテストを実行",
  },
  ko: {
    sharedTitle: "공유된 백테스트 근거",
    sharedDescription: "Backtest Trading의 과거 백테스트 근거를 담은 읽기 전용 스냅샷입니다.",
    readOnly: "보기 전용",
    summaryOnly: "요약만 제공 — 전략 규칙은 포함되지 않음",
    linkAccessNote: "이 미등록 링크를 가진 사람은 만료되거나 소유자가 취소할 때까지 요약을 볼 수 있습니다.",
    openInApp: "Backtest Trading에서 열기",
    openHint: "Safari에 계속 머무르면 메시지나 Mail에서 원본 링크를 열거나 App Store에서 앱을 받으세요.",
    viewOnAppStore: "App Store에서 보기",
    loadingTitle: "공유 근거 불러오는 중",
    loadingBody: "미등록 링크를 확인하고 읽기 전용 보고서를 준비하고 있습니다…",
    notFoundTitle: "이 공유 결과를 사용할 수 없습니다",
    notFoundBody: "링크가 잘못되었거나 사용할 수 없거나 소유자가 취소했을 수 있습니다.",
    expiredTitle: "이 공유 결과가 만료되었습니다",
    expiredBody: "근거 스냅샷을 더 이상 사용할 수 없습니다. 소유자에게 새 링크를 요청하세요.",
    unavailableTitle: "공유 결과를 일시적으로 사용할 수 없습니다",
    unavailableBody: "보고서 서비스가 응답하지 않았습니다. 링크는 변경되지 않았습니다.",
    retry: "다시 시도",
    createdAt: "스냅샷 생성",
    expiresAt: "사용 가능 기한",
    testedPeriod: "테스트 기간",
    returnMetric: "수익률",
    maxDrawdownMetric: "최대 낙폭",
    tradesMetric: "거래 수",
    riskNote: "과거 시뮬레이션은 설명을 위한 근거일 뿐이며, 예측이나 투자 조언이 아닙니다.",
    equityCurve: "정규화 자산 곡선",
    equityDescription: "테스트 기간의 수익률이며 자본 금액이나 거래 시각은 공유하지 않습니다.",
    methodologyBody: "Backtest가 결과를 계산하고 체결 가정을 적용하는 방식을 확인하세요.",
    homepage: "Backtest 홈",
    newBacktest: "새 백테스트 실행",
  },
  ru: {
    sharedTitle: "Общедоступное свидетельство бэктеста",
    sharedDescription: "Снимок исторических результатов Backtest Trading только для просмотра.",
    readOnly: "Только просмотр",
    summaryOnly: "Только сводка — правила стратегии не включены",
    linkAccessNote: "Любой, у кого есть эта незарегистрированная ссылка, может видеть сводку до истечения срока или отзыва владельцем.",
    openInApp: "Открыть в Backtest Trading",
    openHint: "Если страница остается в Safari, откройте исходную ссылку из Сообщений или Mail либо загрузите приложение из App Store.",
    viewOnAppStore: "Открыть в App Store",
    loadingTitle: "Загрузка общего свидетельства",
    loadingBody: "Проверяем незарегистрированную ссылку и готовим отчет только для чтения…",
    notFoundTitle: "Этот общий результат недоступен",
    notFoundBody: "Ссылка может быть неверной, недоступной или отозванной владельцем.",
    expiredTitle: "Срок этой ссылки истек",
    expiredBody: "Снимок больше недоступен. Попросите владельца создать новую ссылку.",
    unavailableTitle: "Общие результаты временно недоступны",
    unavailableBody: "Сервис отчетов не ответил. Ваша ссылка не была изменена.",
    retry: "Повторить",
    createdAt: "Снимок создан",
    expiresAt: "Доступен до",
    testedPeriod: "Период теста",
    returnMetric: "Доходность",
    maxDrawdownMetric: "Максимальная просадка",
    tradesMetric: "Сделки",
    riskNote: "Историческое моделирование — лишь описательное свидетельство, а не прогноз или инвестиционная рекомендация.",
    equityCurve: "Нормализованная кривая капитала",
    equityDescription: "Доходность в процентах за период; суммы капитала и время сделок не публикуются.",
    methodologyBody: "Узнайте, как Backtest рассчитывает результаты и применяет допущения исполнения.",
    homepage: "Главная Backtest",
    newBacktest: "Запустить новый бэктест",
  },
  tr: {
    sharedTitle: "Paylaşılan backtest kanıtı",
    sharedDescription: "Backtest Trading'den geçmiş backtest kanıtının salt okunur anlık görüntüsü.",
    readOnly: "Yalnızca görüntüleme",
    summaryOnly: "Yalnızca özet — strateji kuralları dahil değildir",
    linkAccessNote: "Bu listelenmemiş bağlantıya sahip herkes, süresi dolana veya sahibi iptal edene kadar özeti görebilir.",
    openInApp: "Backtest Trading'de aç",
    openHint: "Sayfa Safari'de kalırsa özgün bağlantıyı Mesajlar veya Mail'den açın ya da uygulamayı App Store'dan indirin.",
    viewOnAppStore: "App Store'da görüntüle",
    loadingTitle: "Paylaşılan kanıt yükleniyor",
    loadingBody: "Listelenmemiş bağlantı kontrol edilip salt okunur rapor hazırlanıyor…",
    notFoundTitle: "Bu paylaşılan sonuç kullanılamıyor",
    notFoundBody: "Bağlantı hatalı, kullanılamıyor veya sahibi tarafından iptal edilmiş olabilir.",
    expiredTitle: "Bu paylaşılan sonucun süresi doldu",
    expiredBody: "Kanıt görüntüsü artık kullanılamıyor. Sahibinden yeni bağlantı isteyin.",
    unavailableTitle: "Paylaşılan sonuçlar geçici olarak kullanılamıyor",
    unavailableBody: "Rapor hizmeti yanıt vermedi. Bağlantınız değiştirilmedi.",
    retry: "Yeniden dene",
    createdAt: "Görüntü oluşturuldu",
    expiresAt: "Geçerlilik sonu",
    testedPeriod: "Test dönemi",
    returnMetric: "Getiri",
    maxDrawdownMetric: "Maksimum düşüş",
    tradesMetric: "İşlemler",
    riskNote: "Geçmiş veriye dayalı simülasyon yalnızca betimleyici kanıttır; tahmin veya yatırım tavsiyesi değildir.",
    equityCurve: "Normalize edilmiş özsermaye eğrisi",
    equityDescription: "Test dönemi yüzdesel getirisi; sermaye tutarları ve işlem zamanları paylaşılmaz.",
    methodologyBody: "Backtest'in sonuçları nasıl hesapladığını ve uygulama varsayımlarını nasıl kullandığını inceleyin.",
    homepage: "Backtest ana sayfası",
    newBacktest: "Yeni backtest çalıştır",
  },
  uk: {
    sharedTitle: "Спільне свідчення бектесту",
    sharedDescription: "Знімок історичних результатів Backtest Trading лише для перегляду.",
    readOnly: "Лише перегляд",
    summaryOnly: "Лише підсумок — правила стратегії не включені",
    linkAccessNote: "Кожен, хто має це непроіндексоване посилання, може бачити підсумок до завершення строку або відкликання власником.",
    openInApp: "Відкрити в Backtest Trading",
    openHint: "Якщо сторінка лишається в Safari, відкрийте початкове посилання з Повідомлень або Mail чи завантажте застосунок з App Store.",
    viewOnAppStore: "Переглянути в App Store",
    loadingTitle: "Завантаження спільного свідчення",
    loadingBody: "Перевіряємо непроіндексоване посилання та готуємо звіт лише для читання…",
    notFoundTitle: "Цей спільний результат недоступний",
    notFoundBody: "Посилання може бути неправильним, недоступним або відкликаним власником.",
    expiredTitle: "Термін цього спільного результату минув",
    expiredBody: "Знімок більше недоступний. Попросіть власника створити нове посилання.",
    unavailableTitle: "Спільні результати тимчасово недоступні",
    unavailableBody: "Сервіс звітів не відповів. Ваше посилання не було змінено.",
    retry: "Спробувати ще раз",
    createdAt: "Знімок створено",
    expiresAt: "Доступний до",
    testedPeriod: "Період тесту",
    returnMetric: "Дохідність",
    maxDrawdownMetric: "Максимальна просадка",
    tradesMetric: "Угоди",
    riskNote: "Історичне моделювання є лише описовим свідченням, а не прогнозом чи інвестиційною порадою.",
    equityCurve: "Нормалізована крива капіталу",
    equityDescription: "Дохідність у відсотках за період; суми капіталу й час угод не публікуються.",
    methodologyBody: "Дізнайтеся, як Backtest розраховує результати й застосовує припущення виконання.",
    homepage: "Головна Backtest",
    newBacktest: "Запустити новий бектест",
  },
};

export const sharedResultFeatureTextKeys = [
  "reproducible",
  "duplicateStrategy",
  "duplicateHint",
  "duplicateFailed",
  "reportResult",
  "reportReason",
  "reportReasonMisleading",
  "reportReasonSpam",
  "reportReasonUnsafe",
  "reportReasonOther",
  "reportDetails",
  "reportSubmit",
  "reportReceived",
  "reportDetailsNotRetained",
  "reportFailed",
  "blockResult",
  "blockedTitle",
  "blockedBody",
  "moderationContact",
  "cancel",
] as const satisfies readonly SharedResultTextKey[];

type SharedResultFeatureTextKey = (typeof sharedResultFeatureTextKeys)[number];

const featureTranslations: Record<
  SharedResultLocale,
  Record<SharedResultFeatureTextKey, string>
> = {
  en: {
    reproducible: "Reusable strategy — review and rerun with your own assumptions",
    duplicateStrategy: "Duplicate strategy",
    duplicateHint: "This copies the published rules into a new editable simulation. It never places a trade.",
    duplicateFailed: "The strategy could not be prepared. Try again later.",
    reportResult: "Report result",
    reportReason: "Reason",
    reportReasonMisleading: "Misleading performance",
    reportReasonSpam: "Spam",
    reportReasonUnsafe: "Unsafe content",
    reportReasonOther: "Other",
    reportDetails: "Optional details",
    reportSubmit: "Send report",
    reportReceived: "Report received. Thank you for helping keep shared research useful.",
    reportDetailsNotRetained: "Report accepted, but optional details were not retained. Contact safety support if those details are important.",
    reportFailed: "The report could not be sent. Check your connection and try again.",
    blockResult: "Block this result",
    blockedTitle: "This shared result is blocked on this device",
    blockedBody: "You can leave this page or run a new independent backtest.",
    moderationContact: "Safety contact",
    cancel: "Cancel",
  },
  vi: {
    reproducible: "Chiến lược có thể dùng lại — hãy kiểm tra và chạy lại với giả định của bạn",
    duplicateStrategy: "Nhân bản chiến lược",
    duplicateHint: "Thao tác này sao chép bộ quy tắc đã công bố vào mô phỏng mới có thể chỉnh sửa. Ứng dụng không đặt lệnh giao dịch.",
    duplicateFailed: "Không thể chuẩn bị chiến lược. Vui lòng thử lại sau.",
    reportResult: "Báo cáo kết quả",
    reportReason: "Lý do",
    reportReasonMisleading: "Hiệu suất gây hiểu lầm",
    reportReasonSpam: "Nội dung rác",
    reportReasonUnsafe: "Nội dung không an toàn",
    reportReasonOther: "Lý do khác",
    reportDetails: "Chi tiết không bắt buộc",
    reportSubmit: "Gửi báo cáo",
    reportReceived: "Đã nhận báo cáo. Cảm ơn bạn đã giúp nội dung nghiên cứu hữu ích hơn.",
    reportDetailsNotRetained: "Báo cáo đã được nhận, nhưng phần chi tiết không bắt buộc chưa được lưu. Hãy liên hệ bộ phận an toàn nếu các chi tiết đó quan trọng.",
    reportFailed: "Không thể gửi báo cáo. Hãy kiểm tra kết nối và thử lại.",
    blockResult: "Chặn kết quả này",
    blockedTitle: "Kết quả chia sẻ này đã bị chặn trên thiết bị",
    blockedBody: "Bạn có thể rời trang hoặc chạy một backtest độc lập mới.",
    moderationContact: "Liên hệ an toàn",
    cancel: "Hủy",
  },
  hi: {
    reproducible: "दोबारा उपयोग योग्य रणनीति — अपनी मान्यताओं के साथ जाँचें और फिर चलाएँ",
    duplicateStrategy: "रणनीति की प्रति बनाएँ",
    duplicateHint: "प्रकाशित नियम एक नए संपादन योग्य सिमुलेशन में कॉपी होते हैं। कोई वास्तविक ट्रेड नहीं होता।",
    duplicateFailed: "रणनीति तैयार नहीं हो सकी। बाद में फिर प्रयास करें।",
    reportResult: "परिणाम की रिपोर्ट करें",
    reportReason: "कारण",
    reportReasonMisleading: "भ्रामक प्रदर्शन",
    reportReasonSpam: "अवांछित सामग्री",
    reportReasonUnsafe: "असुरक्षित सामग्री",
    reportReasonOther: "अन्य",
    reportDetails: "वैकल्पिक विवरण",
    reportSubmit: "रिपोर्ट भेजें",
    reportReceived: "रिपोर्ट मिल गई। साझा शोध को उपयोगी बनाए रखने में मदद के लिए धन्यवाद।",
    reportDetailsNotRetained: "रिपोर्ट स्वीकार हो गई, लेकिन वैकल्पिक विवरण सुरक्षित नहीं रहे। वे महत्वपूर्ण हों तो सुरक्षा सहायता से संपर्क करें।",
    reportFailed: "रिपोर्ट नहीं भेजी जा सकी। अपना कनेक्शन जाँचें और फिर प्रयास करें।",
    blockResult: "इस परिणाम को ब्लॉक करें",
    blockedTitle: "यह साझा परिणाम इस डिवाइस पर ब्लॉक है",
    blockedBody: "आप यह पेज छोड़ सकते हैं या नया स्वतंत्र बैकटेस्ट चला सकते हैं।",
    moderationContact: "सुरक्षा संपर्क",
    cancel: "रद्द करें",
  },
  ur: {
    reproducible: "دوبارہ قابلِ استعمال حکمتِ عملی — اپنی مفروضات کے ساتھ جائزہ لیں اور دوبارہ چلائیں",
    duplicateStrategy: "حکمتِ عملی کی نقل بنائیں",
    duplicateHint: "شائع کردہ قواعد ایک نئی قابلِ تدوین سیمولیشن میں نقل ہوتے ہیں۔ کوئی حقیقی ٹریڈ نہیں ہوتی۔",
    duplicateFailed: "حکمتِ عملی تیار نہیں ہو سکی۔ بعد میں دوبارہ کوشش کریں۔",
    reportResult: "نتیجے کی رپورٹ کریں",
    reportReason: "وجہ",
    reportReasonMisleading: "گمراہ کن کارکردگی",
    reportReasonSpam: "غیر مطلوب مواد",
    reportReasonUnsafe: "غیر محفوظ مواد",
    reportReasonOther: "دیگر",
    reportDetails: "اختیاری تفصیل",
    reportSubmit: "رپورٹ بھیجیں",
    reportReceived: "رپورٹ موصول ہو گئی۔ مشترکہ تحقیق کو مفید رکھنے میں مدد کا شکریہ۔",
    reportDetailsNotRetained: "رپورٹ قبول ہو گئی، لیکن اختیاری تفصیل محفوظ نہیں رہی۔ اگر یہ اہم ہے تو حفاظتی معاونت سے رابطہ کریں۔",
    reportFailed: "رپورٹ نہیں بھیجی جا سکی۔ اپنا کنکشن چیک کرکے دوبارہ کوشش کریں۔",
    blockResult: "اس نتیجے کو بلاک کریں",
    blockedTitle: "یہ مشترکہ نتیجہ اس ڈیوائس پر بلاک ہے",
    blockedBody: "آپ یہ صفحہ چھوڑ سکتے ہیں یا نیا آزاد بیک ٹیسٹ چلا سکتے ہیں۔",
    moderationContact: "حفاظتی رابطہ",
    cancel: "منسوخ کریں",
  },
  th: {
    reproducible: "กลยุทธ์ที่ใช้ซ้ำได้ — ตรวจสอบและรันใหม่ด้วยสมมติฐานของคุณ",
    duplicateStrategy: "ทำสำเนากลยุทธ์",
    duplicateHint: "คัดลอกกฎที่เผยแพร่ไปยังการจำลองใหม่ที่แก้ไขได้ โดยไม่ส่งคำสั่งซื้อขายจริง",
    duplicateFailed: "เตรียมกลยุทธ์ไม่ได้ โปรดลองอีกครั้งภายหลัง",
    reportResult: "รายงานผลลัพธ์",
    reportReason: "เหตุผล",
    reportReasonMisleading: "ผลการทดสอบทำให้เข้าใจผิด",
    reportReasonSpam: "เนื้อหาขยะ",
    reportReasonUnsafe: "เนื้อหาไม่ปลอดภัย",
    reportReasonOther: "อื่น ๆ",
    reportDetails: "รายละเอียดเพิ่มเติม (ไม่บังคับ)",
    reportSubmit: "ส่งรายงาน",
    reportReceived: "ได้รับรายงานแล้ว ขอบคุณที่ช่วยให้การวิจัยที่แชร์มีประโยชน์",
    reportDetailsNotRetained: "รับรายงานแล้ว แต่ไม่ได้เก็บรายละเอียดเพิ่มเติม หากรายละเอียดนั้นสำคัญ โปรดติดต่อฝ่ายความปลอดภัย",
    reportFailed: "ส่งรายงานไม่ได้ โปรดตรวจสอบการเชื่อมต่อแล้วลองอีกครั้ง",
    blockResult: "บล็อกผลลัพธ์นี้",
    blockedTitle: "ผลลัพธ์ที่แชร์นี้ถูกบล็อกบนอุปกรณ์นี้",
    blockedBody: "คุณสามารถออกจากหน้านี้หรือรันแบ็กเทสต์ใหม่แบบอิสระได้",
    moderationContact: "ช่องทางติดต่อด้านความปลอดภัย",
    cancel: "ยกเลิก",
  },
  bn: {
    reproducible: "পুনর্ব্যবহারযোগ্য কৌশল — আপনার অনুমান দিয়ে যাচাই করে আবার চালান",
    duplicateStrategy: "কৌশলের অনুলিপি তৈরি করুন",
    duplicateHint: "প্রকাশিত নিয়ম নতুন সম্পাদনাযোগ্য সিমুলেশনে কপি হয়। কোনো বাস্তব ট্রেড হয় না।",
    duplicateFailed: "কৌশলটি প্রস্তুত করা যায়নি। পরে আবার চেষ্টা করুন।",
    reportResult: "ফলাফল রিপোর্ট করুন",
    reportReason: "কারণ",
    reportReasonMisleading: "বিভ্রান্তিকর ফলাফল",
    reportReasonSpam: "অপ্রয়োজনীয় বার্তা",
    reportReasonUnsafe: "অনিরাপদ বিষয়বস্তু",
    reportReasonOther: "অন্যান্য",
    reportDetails: "ঐচ্ছিক বিবরণ",
    reportSubmit: "রিপোর্ট পাঠান",
    reportReceived: "রিপোর্ট পাওয়া গেছে। শেয়ার করা গবেষণা উপযোগী রাখতে সহায়তার জন্য ধন্যবাদ।",
    reportDetailsNotRetained: "রিপোর্ট গ্রহণ করা হয়েছে, তবে ঐচ্ছিক বিবরণ রাখা হয়নি। বিবরণটি গুরুত্বপূর্ণ হলে নিরাপত্তা সহায়তায় যোগাযোগ করুন।",
    reportFailed: "রিপোর্ট পাঠানো যায়নি। সংযোগ পরীক্ষা করে আবার চেষ্টা করুন।",
    blockResult: "এই ফলাফল ব্লক করুন",
    blockedTitle: "এই শেয়ার করা ফলাফলটি এই ডিভাইসে ব্লক করা হয়েছে",
    blockedBody: "আপনি পৃষ্ঠা ছাড়তে বা নতুন স্বাধীন ব্যাকটেস্ট চালাতে পারেন।",
    moderationContact: "নিরাপত্তা যোগাযোগ",
    cancel: "বাতিল",
  },
  id: {
    reproducible: "Strategi yang dapat digunakan ulang — tinjau dan jalankan kembali dengan asumsi Anda",
    duplicateStrategy: "Duplikasikan strategi",
    duplicateHint: "Aturan yang diterbitkan disalin ke simulasi baru yang dapat diedit. Tidak ada transaksi sungguhan.",
    duplicateFailed: "Strategi tidak dapat disiapkan. Coba lagi nanti.",
    reportResult: "Laporkan hasil",
    reportReason: "Alasan",
    reportReasonMisleading: "Kinerja menyesatkan",
    reportReasonSpam: "Konten sampah",
    reportReasonUnsafe: "Konten tidak aman",
    reportReasonOther: "Lainnya",
    reportDetails: "Detail opsional",
    reportSubmit: "Kirim laporan",
    reportReceived: "Laporan diterima. Terima kasih telah membantu menjaga riset bersama tetap berguna.",
    reportDetailsNotRetained: "Laporan diterima, tetapi detail opsional tidak disimpan. Hubungi dukungan keamanan jika detail tersebut penting.",
    reportFailed: "Laporan tidak dapat dikirim. Periksa koneksi Anda lalu coba lagi.",
    blockResult: "Blokir hasil ini",
    blockedTitle: "Hasil bersama ini diblokir di perangkat ini",
    blockedBody: "Anda dapat meninggalkan halaman ini atau menjalankan backtest baru yang independen.",
    moderationContact: "Kontak keamanan",
    cancel: "Batal",
  },
  fil: {
    reproducible: "Strategy na magagamit muli — suriin at patakbuhin gamit ang sarili mong mga palagay",
    duplicateStrategy: "Gumawa ng kopya ng strategy",
    duplicateHint: "Kinokopya ang inilathalang mga tuntunin sa bagong nae-edit na simulation. Hindi ito naglalagay ng totoong trade.",
    duplicateFailed: "Hindi maihanda ang strategy. Subukan muli mamaya.",
    reportResult: "I-report ang resulta",
    reportReason: "Dahilan",
    reportReasonMisleading: "Mapanlinlang na performance",
    reportReasonSpam: "Basurang nilalaman",
    reportReasonUnsafe: "Hindi ligtas na nilalaman",
    reportReasonOther: "Iba pa",
    reportDetails: "Opsyonal na detalye",
    reportSubmit: "Ipadala ang report",
    reportReceived: "Natanggap ang report. Salamat sa pagtulong na mapanatiling kapaki-pakinabang ang ibinahaging research.",
    reportDetailsNotRetained: "Tinanggap ang report, pero hindi na-save ang opsyonal na detalye. Kontakin ang safety support kung mahalaga ang detalyeng iyon.",
    reportFailed: "Hindi naipadala ang report. Tingnan ang koneksyon at subukan muli.",
    blockResult: "I-block ang resultang ito",
    blockedTitle: "Naka-block ang ibinahaging resultang ito sa device na ito",
    blockedBody: "Maaari mong iwan ang page o magpatakbo ng bagong hiwalay na backtest.",
    moderationContact: "Kontak para sa kaligtasan",
    cancel: "Kanselahin",
  },
  "es-419": {
    reproducible: "Estrategia reutilizable — revísala y ejecútala con tus propios supuestos",
    duplicateStrategy: "Duplicar estrategia",
    duplicateHint: "Copia las reglas publicadas a una simulación nueva y editable. Nunca coloca una operación real.",
    duplicateFailed: "No se pudo preparar la estrategia. Inténtalo más tarde.",
    reportResult: "Reportar resultado",
    reportReason: "Motivo",
    reportReasonMisleading: "Rendimiento engañoso",
    reportReasonSpam: "Contenido no deseado",
    reportReasonUnsafe: "Contenido inseguro",
    reportReasonOther: "Otro",
    reportDetails: "Detalles opcionales",
    reportSubmit: "Enviar reporte",
    reportReceived: "Recibimos el reporte. Gracias por ayudar a mantener útil la investigación compartida.",
    reportDetailsNotRetained: "El reporte fue aceptado, pero no se conservaron los detalles opcionales. Contacta a soporte de seguridad si son importantes.",
    reportFailed: "No se pudo enviar el reporte. Revisa tu conexión e inténtalo de nuevo.",
    blockResult: "Bloquear este resultado",
    blockedTitle: "Este resultado compartido está bloqueado en este dispositivo",
    blockedBody: "Puedes salir de esta página o ejecutar un backtest nuevo e independiente.",
    moderationContact: "Contacto de seguridad",
    cancel: "Cancelar",
  },
  "pt-BR": {
    reproducible: "Estratégia reutilizável — revise e execute novamente com suas próprias premissas",
    duplicateStrategy: "Duplicar estratégia",
    duplicateHint: "Copia as regras publicadas para uma nova simulação editável. Nunca envia uma ordem real.",
    duplicateFailed: "Não foi possível preparar a estratégia. Tente novamente mais tarde.",
    reportResult: "Denunciar resultado",
    reportReason: "Motivo",
    reportReasonMisleading: "Desempenho enganoso",
    reportReasonSpam: "Conteúdo indesejado",
    reportReasonUnsafe: "Conteúdo inseguro",
    reportReasonOther: "Outro",
    reportDetails: "Detalhes opcionais",
    reportSubmit: "Enviar denúncia",
    reportReceived: "Denúncia recebida. Obrigado por ajudar a manter útil a pesquisa compartilhada.",
    reportDetailsNotRetained: "A denúncia foi aceita, mas os detalhes opcionais não foram mantidos. Contate o suporte de segurança se forem importantes.",
    reportFailed: "Não foi possível enviar a denúncia. Verifique sua conexão e tente novamente.",
    blockResult: "Bloquear este resultado",
    blockedTitle: "Este resultado compartilhado está bloqueado neste dispositivo",
    blockedBody: "Você pode sair desta página ou executar um novo backtest independente.",
    moderationContact: "Contato de segurança",
    cancel: "Cancelar",
  },
  ja: {
    reproducible: "再利用可能な戦略 — 自分の前提で確認して再実行",
    duplicateStrategy: "戦略を複製",
    duplicateHint: "公開されたルールを編集可能な新しいシミュレーションにコピーします。実取引は行いません。",
    duplicateFailed: "戦略を準備できませんでした。後でもう一度お試しください。",
    reportResult: "結果を報告",
    reportReason: "理由",
    reportReasonMisleading: "誤解を招く成績",
    reportReasonSpam: "迷惑な内容",
    reportReasonUnsafe: "安全でない内容",
    reportReasonOther: "その他",
    reportDetails: "任意の詳細",
    reportSubmit: "報告を送信",
    reportReceived: "報告を受け付けました。共有研究の品質向上にご協力いただきありがとうございます。",
    reportDetailsNotRetained: "報告は受け付けましたが、任意の詳細は保存されませんでした。詳細が重要な場合は安全サポートへご連絡ください。",
    reportFailed: "報告を送信できませんでした。接続を確認してもう一度お試しください。",
    blockResult: "この結果をブロック",
    blockedTitle: "この共有結果はこのデバイスでブロックされています",
    blockedBody: "このページを閉じるか、新しい独立したバックテストを実行できます。",
    moderationContact: "安全に関する連絡先",
    cancel: "キャンセル",
  },
  ko: {
    reproducible: "재사용 가능한 전략 — 내 가정으로 검토하고 다시 실행",
    duplicateStrategy: "전략 복제",
    duplicateHint: "공개된 규칙을 편집 가능한 새 시뮬레이션으로 복사합니다. 실제 거래는 하지 않습니다.",
    duplicateFailed: "전략을 준비하지 못했습니다. 나중에 다시 시도하세요.",
    reportResult: "결과 신고",
    reportReason: "이유",
    reportReasonMisleading: "오해를 부르는 성과",
    reportReasonSpam: "불필요한 콘텐츠",
    reportReasonUnsafe: "안전하지 않은 콘텐츠",
    reportReasonOther: "기타",
    reportDetails: "선택 사항",
    reportSubmit: "신고 보내기",
    reportReceived: "신고를 접수했습니다. 공유 연구를 유용하게 유지하는 데 도움을 주셔서 감사합니다.",
    reportDetailsNotRetained: "신고는 접수되었지만 선택 입력한 세부 내용은 보관되지 않았습니다. 중요하다면 안전 지원팀에 문의하세요.",
    reportFailed: "신고를 보내지 못했습니다. 연결을 확인하고 다시 시도하세요.",
    blockResult: "이 결과 차단",
    blockedTitle: "이 공유 결과는 이 기기에서 차단되었습니다",
    blockedBody: "이 페이지를 나가거나 새로운 독립 백테스트를 실행할 수 있습니다.",
    moderationContact: "안전 문의",
    cancel: "취소",
  },
  ru: {
    reproducible: "Повторно используемая стратегия — проверьте и запустите со своими допущениями",
    duplicateStrategy: "Дублировать стратегию",
    duplicateHint: "Публикуемые правила копируются в новую редактируемую симуляцию. Реальные сделки не совершаются.",
    duplicateFailed: "Не удалось подготовить стратегию. Повторите попытку позже.",
    reportResult: "Пожаловаться на результат",
    reportReason: "Причина",
    reportReasonMisleading: "Вводящие в заблуждение результаты",
    reportReasonSpam: "Нежелательный контент",
    reportReasonUnsafe: "Небезопасный контент",
    reportReasonOther: "Другое",
    reportDetails: "Необязательные подробности",
    reportSubmit: "Отправить жалобу",
    reportReceived: "Жалоба получена. Спасибо, что помогаете сохранять пользу общих исследований.",
    reportDetailsNotRetained: "Жалоба принята, но необязательные подробности не сохранены. Если они важны, обратитесь в службу безопасности.",
    reportFailed: "Не удалось отправить жалобу. Проверьте подключение и повторите попытку.",
    blockResult: "Заблокировать результат",
    blockedTitle: "Этот общий результат заблокирован на данном устройстве",
    blockedBody: "Можно покинуть страницу или запустить новый независимый бэктест.",
    moderationContact: "Контакт по безопасности",
    cancel: "Отмена",
  },
  tr: {
    reproducible: "Yeniden kullanılabilir strateji — kendi varsayımlarınızla inceleyip çalıştırın",
    duplicateStrategy: "Stratejiyi çoğalt",
    duplicateHint: "Yayımlanan kuralları düzenlenebilir yeni bir simülasyona kopyalar. Gerçek işlem yapmaz.",
    duplicateFailed: "Strateji hazırlanamadı. Daha sonra yeniden deneyin.",
    reportResult: "Sonucu bildir",
    reportReason: "Neden",
    reportReasonMisleading: "Yanıltıcı performans",
    reportReasonSpam: "İstenmeyen içerik",
    reportReasonUnsafe: "Güvenli olmayan içerik",
    reportReasonOther: "Diğer",
    reportDetails: "İsteğe bağlı ayrıntılar",
    reportSubmit: "Bildirimi gönder",
    reportReceived: "Bildirim alındı. Paylaşılan araştırmayı yararlı tutmaya yardımcı olduğunuz için teşekkürler.",
    reportDetailsNotRetained: "Bildirim kabul edildi ancak isteğe bağlı ayrıntılar saklanmadı. Önemliyse güvenlik desteğiyle iletişime geçin.",
    reportFailed: "Bildirim gönderilemedi. Bağlantınızı kontrol edip yeniden deneyin.",
    blockResult: "Bu sonucu engelle",
    blockedTitle: "Bu paylaşılan sonuç bu cihazda engellendi",
    blockedBody: "Bu sayfadan ayrılabilir veya yeni ve bağımsız bir backtest çalıştırabilirsiniz.",
    moderationContact: "Güvenlik iletişimi",
    cancel: "İptal",
  },
  uk: {
    reproducible: "Стратегія для повторного використання — перевірте й запустіть зі своїми припущеннями",
    duplicateStrategy: "Дублювати стратегію",
    duplicateHint: "Опубліковані правила копіюються в нову редаговану симуляцію. Реальні угоди не виконуються.",
    duplicateFailed: "Не вдалося підготувати стратегію. Спробуйте пізніше.",
    reportResult: "Поскаржитися на результат",
    reportReason: "Причина",
    reportReasonMisleading: "Оманливі результати",
    reportReasonSpam: "Небажаний вміст",
    reportReasonUnsafe: "Небезпечний вміст",
    reportReasonOther: "Інше",
    reportDetails: "Необов’язкові подробиці",
    reportSubmit: "Надіслати скаргу",
    reportReceived: "Скаргу отримано. Дякуємо, що допомагаєте зберігати користь спільних досліджень.",
    reportDetailsNotRetained: "Скаргу прийнято, але необов’язкові подробиці не збережено. Якщо вони важливі, зверніться до служби безпеки.",
    reportFailed: "Не вдалося надіслати скаргу. Перевірте з’єднання й повторіть спробу.",
    blockResult: "Заблокувати результат",
    blockedTitle: "Цей спільний результат заблоковано на цьому пристрої",
    blockedBody: "Можна залишити сторінку або запустити новий незалежний бектест.",
    moderationContact: "Контакт із питань безпеки",
    cancel: "Скасувати",
  },
};

export function hasLocalizedSharedResultFeatureText(
  locale: SharedResultLocale,
  key: SharedResultFeatureTextKey,
): boolean {
  return Object.prototype.hasOwnProperty.call(featureTranslations[locale], key);
}

export function sharedResultText(locale: SharedResultLocale, key: SharedResultTextKey): string {
  if ((sharedResultFeatureTextKeys as readonly string[]).includes(key)) {
    return featureTranslations[locale][key as SharedResultFeatureTextKey];
  }
  return locale === "en" ? en[key] : translations[locale][key] || en[key];
}

export function sharedResultMetadata(locale: SharedResultLocale, symbol?: string, timeframe?: string) {
  const title = symbol && timeframe
    ? `${symbol} · ${timeframe} — ${sharedResultText(locale, "sharedTitle")}`
    : sharedResultText(locale, "sharedTitle");
  return {
    title,
    description: sharedResultText(locale, "sharedDescription"),
  };
}
